import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { Model, CreateModelForm } from '../../types';
import { useProjectStore } from '../../store';
import { useCreateModel } from '../../hooks/queries/useModelMutations';
import CustomSelect from '../common/CustomSelect';

// Available Pydantic types
const BASE_TYPES = [
  { label: 'String', value: 'str' },
  { label: 'Integer', value: 'int' },
  { label: 'Float', value: 'float' },
  { label: 'Boolean', value: 'bool' },
  { label: 'Datetime', value: 'datetime' },
  { label: 'Literal', value: 'Literal' },
  { label: 'List', value: 'list' },
];

interface Property {
  name: string;
  type: string;
  literalValues?: string[];
  listElementType?: string; // For list properties
}

interface InlineModelCreatorProps {
  existingModels: Model[];
  onModelCreated: (model: Model) => void;
  onCancel: () => void;
  isExpanded?: boolean;
  onToggleExpanded?: () => void;
  isCreating?: boolean;
  projectId?: string;
}

const InlineModelCreator: React.FC<InlineModelCreatorProps> = ({
  existingModels,
  onModelCreated,
  onCancel,
  isExpanded = false,
  onToggleExpanded,
  isCreating = false,
  projectId
}) => {
  // AC-1412 / AC-1377 P5.1: model creation flows through the
  // `useCreateModel` mutation hook instead of `useProjectStore.createModel`.
  // The hook owns invalidation of `projectModels` and `projectRegistries`
  // on success per TD section 2.6, replacing the dual-write the store used
  // to perform.
  const currentProject = useProjectStore(state => state.currentProject);
  const effectiveOperationId = projectId || currentProject?.project_id;
  const createModelMutation = useCreateModel(effectiveOperationId ?? '');
  
  const [className, setClassName] = useState('');
  const [description, setDescription] = useState('');
  const [properties, setProperties] = useState<Property[]>([]);
  const [literalInputs, setLiteralInputs] = useState<Record<number, string>>({});
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [focusNewProperty, setFocusNewProperty] = useState(false);
  const lastPropertyRef = useRef<HTMLInputElement>(null);

  // Debug effect to track button state
  useEffect(() => {
    console.log('InlineModelCreator: Button disabled state:', loading || isCreating);
  }, [loading, isCreating]);

  const validateForm = (): boolean => {
    console.log('InlineModelCreator: validateForm called');
    console.log('InlineModelCreator: Current form state - className:', className, 'properties:', properties);
    
    const newErrors: string[] = [];

    // Validate class name
    if (!className) {
      newErrors.push('Class name is required');
    } else if (!/^[A-Z][a-zA-Z0-9]*$/.test(className)) {
      newErrors.push('Class name must start with a capital letter and contain only letters and numbers');
    } else if (existingModels.some(m => m.class_name === className)) {
      newErrors.push('A model with this class name already exists');
    }

    // Validate properties
    properties.forEach((prop, idx) => {
      if (!prop.name) {
        newErrors.push(`Property ${idx + 1} name is required`);
      } else if (!/^[a-z][a-zA-Z0-9_]*$/.test(prop.name)) {
        newErrors.push(`Property ${idx + 1} name must start with a lowercase letter and contain only letters, numbers, and underscores`);
      }
      if (!prop.type) {
        newErrors.push(`Property ${idx + 1} type is required`);
      }
      if (prop.type === 'Literal' && (!prop.literalValues || prop.literalValues.length === 0)) {
        newErrors.push(`Property ${idx + 1} requires at least one literal value`);
      }
      if (prop.type === 'list' && !prop.listElementType) {
        newErrors.push(`Property ${idx + 1} requires an element type for the list`);
      }
    });

    console.log('InlineModelCreator: Validation errors:', newErrors);
    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handlePropertyChange = (idx: number, field: keyof Property, value: string) => {
    setProperties(prev => {
      const newProps = [...prev];
      if (field === 'type') {
        if (value !== 'Literal') {
          delete newProps[idx].literalValues;
        }
        if (value !== 'list') {
          delete newProps[idx].listElementType;
        }
      }
      newProps[idx] = { ...newProps[idx], [field]: value };
      return newProps;
    });
  };

  const handleAddProperty = () => {
    setProperties(prev => [...prev, { name: '', type: '' }]);
    setFocusNewProperty(true);
  };

  useEffect(() => {
    if (focusNewProperty && lastPropertyRef.current) {
      lastPropertyRef.current.focus();
      setFocusNewProperty(false);
    }
  }, [focusNewProperty, properties.length]);

  const handleRemoveProperty = (idx: number) => {
    setProperties(prev => prev.filter((_, i) => i !== idx));
  };

  const handleAddLiteralValue = (idx: number) => {
    const value = (literalInputs[idx] || '').trim();
    if (!value) return;
    setProperties(prev => {
      const newProps = [...prev];
      const values = newProps[idx].literalValues || [];
      if (!values.includes(value)) {
        newProps[idx].literalValues = [...values, value];
      }
      return newProps;
    });
    setLiteralInputs(prev => ({ ...prev, [idx]: '' }));
  };

  const handleRemoveLiteralValue = (idx: number, value: string) => {
    setProperties(prev => {
      const newProps = [...prev];
      newProps[idx].literalValues = (newProps[idx].literalValues || []).filter(v => v !== value);
      return newProps;
    });
  };

  const handleLiteralInputChange = (idx: number, value: string) => {
    setLiteralInputs(prev => ({ ...prev, [idx]: value }));
  };

  const handleSubmit = async () => {
    console.log('InlineModelCreator: handleSubmit called');
    console.log('InlineModelCreator: Current state - loading:', loading, 'isCreating:', isCreating, 'effectiveOperationId:', effectiveOperationId);
    
    if (!validateForm()) {
      console.log('InlineModelCreator: Form validation failed');
      return;
    }

    if (!effectiveOperationId) {
      console.log('InlineModelCreator: No effective project');
      setErrors(['No project selected']);
      return;
    }

    console.log('InlineModelCreator: Starting model creation...');
    setLoading(true);
    try {
      const modelData: CreateModelForm = {
        class_name: className,
        description,
        properties: Object.fromEntries(
          properties.map(prop => [
            prop.name,
            prop.type === 'Literal' && prop.literalValues
              ? `Literal[${prop.literalValues.map(v => `'${v}'`).join(', ')}]`
              : prop.type === 'list' && prop.listElementType
              ? `list[${prop.listElementType}]`
              : prop.type
          ])
        )
      };

      console.log('InlineModelCreator: Creating model with data:', modelData);
      const newModel = await createModelMutation.mutateAsync({ data: modelData });
      console.log('InlineModelCreator: Model creation result:', newModel);
      
      if (newModel) {
        console.log('InlineModelCreator: Model created successfully, calling onModelCreated');
        // Reset form
        setClassName('');
        setDescription('');
        setProperties([]);
        setErrors([]);
        
        // Call the callback with the new model
        onModelCreated(newModel);
      }
    } catch (error) {
      console.error('Error creating model:', error);
      setErrors(['Failed to create model. Please try again.']);
    } finally {
      console.log('InlineModelCreator: Setting loading to false');
      setLoading(false);
    }
  };

  const generatePreview = () => {
    const props = properties.map(prop => {
      if (prop.type === 'Literal' && prop.literalValues) {
        return `    ${prop.name}: Literal[${prop.literalValues.map(v => `'${v}'`).join(', ')}]`;
      } else if (prop.type === 'list' && prop.listElementType) {
        return `    ${prop.name}: list[${prop.listElementType}]`;
      }
      return `    ${prop.name}: ${prop.type}`;
    }).join('\n');

    return `class ${className}:\n${props}`;
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-2">
          {onToggleExpanded && (
            <button
              type="button"
              onClick={onToggleExpanded}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300"
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
          )}
          <h4 className="text-sm font-medium text-gray-900 dark:text-warm-white">Create New Schema</h4>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300"
        >
          Cancel
        </button>
      </div>

      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* Error Messages */}
          {errors.length > 0 && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
              {errors.map((error, idx) => (
                <p key={idx} className="text-sm text-red-600 dark:text-red-400 flex items-center">
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  {error}
                </p>
              ))}
            </div>
          )}

          {/* Schema Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Schema Name *
            </label>
            <input
              type="text"
              value={className}
              onChange={(e) => setClassName(e.target.value)}
              autoComplete="off"
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="e.g., UserProfile"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Must start with a capital letter and contain only letters and numbers
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              autoComplete="off"
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              rows={2}
              placeholder="Describe the purpose of this model..."
            />
          </div>

          {/* Properties */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Properties
              </label>
              <button
                type="button"
                onClick={handleAddProperty}
                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary-600 hover:text-primary-700"
              >
                <Plus className="w-3 h-3" />
                Add Property
              </button>
            </div>

            <div className="space-y-2">
              {properties.map((prop, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 bg-white dark:bg-cap-dark border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex-1 flex items-center gap-2">
                    <input
                      ref={idx === properties.length - 1 ? lastPropertyRef : undefined}
                      type="text"
                      value={prop.name}
                      onChange={(e) => handlePropertyChange(idx, 'name', e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (prop.name.trim()) {
                            handleAddProperty();
                          }
                        }
                      }}
                      className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Property name"
                    />
                    <CustomSelect
                      value={prop.type}
                      onChange={(value) => handlePropertyChange(idx, 'type', value)}
                      options={[
                        ...BASE_TYPES.map((type) => ({ value: type.value, label: type.label })),
                        ...existingModels.map((model) => ({ value: model.class_name, label: model.class_name })),
                      ]}
                      placeholder="Select type"
                      className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    {prop.type === 'list' && (
                      <CustomSelect
                        value={prop.listElementType || ''}
                        onChange={(value) => handlePropertyChange(idx, 'listElementType', value)}
                        options={[
                          ...BASE_TYPES.map((type) => ({ value: type.value, label: type.label })),
                          ...existingModels.map((model) => ({ value: model.class_name, label: model.class_name })),
                        ]}
                        placeholder="Select element type"
                        className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveProperty(idx)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}

              {/* Add Property button below last property */}
              <button
                type="button"
                onClick={handleAddProperty}
                className="flex items-center gap-1 w-full px-3 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-800 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Property
              </button>
            </div>

            {/* Literal Values for Literal type */}
            {properties.map((prop, idx) => 
              prop.type === 'Literal' ? (
                <div key={`literal-${idx}`} className="ml-4 p-2 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-700 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <label className="text-xs font-medium text-blue-700 dark:text-blue-300">
                      Literal values for {prop.name}:
                    </label>
                    <div className="flex items-center gap-1">
                      <input
                        type="text"
                        value={literalInputs[idx] || ''}
                        onChange={(e) => handleLiteralInputChange(idx, e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddLiteralValue(idx))}
                        className="flex-1 px-2 py-1 text-xs border border-primary-300 dark:border-primary-600 rounded dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Enter value"
                      />
                      <button
                        type="button"
                        onClick={() => handleAddLiteralValue(idx)}
                        className="px-2 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                  {prop.literalValues && prop.literalValues.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {prop.literalValues.map((value, valueIdx) => (
                        <span
                          key={valueIdx}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded"
                        >
                          {value}
                          <button
                            type="button"
                            onClick={() => handleRemoveLiteralValue(idx, value)}
                            className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ) : null
            )}
          </div>

          {/* Model Preview */}
          {className && properties.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Preview
              </label>
              <pre className="p-3 bg-gray-900 text-gray-100 rounded-lg overflow-x-auto text-xs font-mono">
                {generatePreview()}
              </pre>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => {
                console.log('InlineModelCreator: Create Model button clicked');
                console.log('InlineModelCreator: Button state - loading:', loading, 'isCreating:', isCreating);
                handleSubmit();
              }}
              disabled={loading || isCreating}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading || isCreating ? 'Creating...' : 'Create Schema'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InlineModelCreator; 